import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Button, Typography } from '@mui/material';
import { alpha, keyframes } from '@mui/material/styles';
import useStore from '../../store/useStore';
import { orchard } from '../../theme';
import { BY_KEY } from './lessons';

/* «Попробовать» in «Обучение» opens the real screen with ?coach=<lesson>:
   the screen dims around the button to press and a bubble says what to do.
   Pressing that button (or «Понятно») closes it; no button found → just the
   bubble in the middle. */

const breathe = keyframes`
  0%, 100% { box-shadow: 0 0 0 3px ${orchard.honey}, 0 0 0 9px ${alpha(orchard.honey, 0.35)}; }
  50% { box-shadow: 0 0 0 3px ${orchard.honey}, 0 0 0 15px ${alpha(orchard.honey, 0.05)}; }
`;
const rise = keyframes`
  from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; }
`;

const PAD = 8;
const Z = 1450; // over the app bar and dialogs, under tooltips

function visible(el) {
  const r = el.getBoundingClientRect();
  return (
    r.width > 0 && r.height > 0 && getComputedStyle(el).visibility !== 'hidden'
  );
}

export function findTarget(spec) {
  if (!spec) return null;
  if (spec.selector) {
    const el = [...document.querySelectorAll(spec.selector)].find(visible);
    if (el) return el;
  }
  for (const a of spec.aria || []) {
    const el = [...document.querySelectorAll(`[aria-label="${a}"]`)].find(
      visible
    );
    if (el) return el;
  }
  if (spec.text) {
    const els = [
      ...document.querySelectorAll('button, a, [role=button], [role=tab]'),
    ];
    for (const t of spec.text) {
      const el = els.find(
        (e) => visible(e) && (e.innerText || '').trim().startsWith(t)
      );
      if (el) return el;
    }
  }
  return null;
}

export default function CoachMark() {
  const location = useLocation();
  const navigate = useNavigate();
  const { language } = useStore();
  const en = language === 'en';
  const [active, setActive] = useState(null); // lesson key
  const [rect, setRect] = useState(null);
  const [ready, setReady] = useState(false);
  const elRef = useRef(null);
  const bubbleRef = useRef(null);
  const [bubbleH, setBubbleH] = useState(260);
  const path = useRef(location.pathname);

  useLayoutEffect(() => {
    const b = bubbleRef.current;
    if (b && Math.abs(b.offsetHeight - bubbleH) > 2) setBubbleH(b.offsetHeight);
  });

  // ?coach=<key> starts it (pages may clean their URL right after — keep it)
  useEffect(() => {
    const key = new URLSearchParams(location.search).get('coach');
    if (key && BY_KEY[key] && location.pathname !== '/learn') setActive(key);
  }, [location.search, location.pathname]);
  useEffect(() => {
    if (path.current !== location.pathname) {
      path.current = location.pathname;
      const key = new URLSearchParams(location.search).get('coach');
      if (!key) setActive(null);
    }
  }, [location.pathname, location.search]);

  const close = useCallback(() => {
    setActive(null);
    setRect(null);
    setReady(false);
    elRef.current = null;
    const p = new URLSearchParams(window.location.search);
    if (p.has('coach')) {
      p.delete('coach');
      navigate(
        {
          pathname: window.location.pathname,
          search: p.toString() ? `?${p}` : '',
        },
        { replace: true }
      );
    }
  }, [navigate]);

  const measure = useCallback(() => {
    const el = elRef.current;
    if (!el || !el.isConnected) return;
    const r = el.getBoundingClientRect();
    setRect({ x: r.left, y: r.top, w: r.width, h: r.height });
  }, []);

  // find the button (pages load their data first), scroll to it
  useEffect(() => {
    if (!active) return undefined;
    const lesson = BY_KEY[active];
    let tries = 0;
    let settle = null;
    setReady(false);
    setRect(null);
    elRef.current = null;
    const t = setInterval(() => {
      tries += 1;
      const el = findTarget(lesson.try && lesson.try.target);
      if (el) {
        clearInterval(t);
        elRef.current = el;
        el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        settle = setTimeout(() => {
          measure();
          setReady(true);
        }, 450);
      } else if (tries > 24) {
        clearInterval(t);
        setReady(true);
      }
    }, 300);
    return () => {
      clearInterval(t);
      clearTimeout(settle);
    };
  }, [active, measure]);

  // follow the button; pressing it ends the coaching (the press still goes through)
  useEffect(() => {
    if (!ready || !elRef.current) return undefined;
    const el = elRef.current;
    const onMove = () => measure();
    const onPress = () => setTimeout(close, 0);
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    el.addEventListener('pointerdown', onPress);
    const watch = setInterval(measure, 800); // layout shifts (images, data)
    return () => {
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
      el.removeEventListener('pointerdown', onPress);
      clearInterval(watch);
    };
  }, [ready, measure, close]);

  useEffect(() => {
    if (!active) return undefined;
    const onKey = (e) => e.key === 'Escape' && close();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, close]);

  if (!active || !ready) return null;
  const lesson = BY_KEY[active];
  const L = (o) => (o ? (en ? o.en : o.ru) : '');
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const hole = rect && {
    x: Math.max(0, rect.x - PAD),
    y: Math.max(0, rect.y - PAD),
    w: rect.w + PAD * 2,
    h: rect.h + PAD * 2,
  };
  const shade = {
    position: 'fixed',
    bgcolor: 'rgba(20, 12, 24, 0.62)',
    zIndex: Z,
  };
  const bubbleW = Math.min(360, vw - 24);
  const GAP = 14;
  let top = Math.max(24, (vh - bubbleH) / 2);
  if (hole) {
    if (vh - (hole.y + hole.h) - GAP - 8 >= bubbleH)
      top = hole.y + hole.h + GAP;
    else if (hole.y - GAP - 8 >= bubbleH) top = hole.y - GAP - bubbleH;
    else top = Math.max(8, vh - bubbleH - 8);
  }
  const bubble = {
    top,
    left: hole
      ? Math.min(
          Math.max(12, hole.x + hole.w / 2 - bubbleW / 2),
          vw - bubbleW - 12
        )
      : (vw - bubbleW) / 2,
  };

  return createPortal(
    <Box data-coach-mark={active}>
      {hole ? (
        <>
          <Box
            sx={{ ...shade, left: 0, top: 0, width: '100vw', height: hole.y }}
            onClick={close}
          />
          <Box
            sx={{
              ...shade,
              left: 0,
              top: hole.y + hole.h,
              width: '100vw',
              bottom: 0,
            }}
            onClick={close}
          />
          <Box
            sx={{
              ...shade,
              left: 0,
              top: hole.y,
              width: hole.x,
              height: hole.h,
            }}
            onClick={close}
          />
          <Box
            sx={{
              ...shade,
              left: hole.x + hole.w,
              top: hole.y,
              right: 0,
              height: hole.h,
            }}
            onClick={close}
          />
          <Box
            data-coach-hole
            sx={{
              position: 'fixed',
              left: hole.x,
              top: hole.y,
              width: hole.w,
              height: hole.h,
              borderRadius: '14px',
              pointerEvents: 'none',
              zIndex: Z,
              animation: `${breathe} 1.8s ease-in-out infinite`,
            }}
          />
        </>
      ) : (
        <Box sx={{ ...shade, inset: 0 }} onClick={close} />
      )}
      <Box
        ref={bubbleRef}
        role="dialog"
        aria-label={L(lesson.title)}
        sx={{
          position: 'fixed',
          zIndex: Z + 1,
          width: bubbleW,
          ...bubble,
          bgcolor: 'background.paper',
          borderRadius: '18px',
          p: 2,
          boxShadow: '0 24px 60px -20px rgba(0,0,0,.6)',
          animation: `${rise} .35s ease both`,
        }}
      >
        <Typography
          variant="overline"
          sx={{ color: orchard.cherry, fontWeight: 800, fontSize: '0.64rem' }}
        >
          {en ? 'Try it' : 'Попробуйте'}
        </Typography>
        <Typography sx={{ fontWeight: 800, lineHeight: 1.3, mb: 1 }}>
          {L(lesson.title)}
        </Typography>
        <Box component="ol" sx={{ m: 0, pl: 2.5, display: 'grid', gap: 0.5 }}>
          {lesson.steps.map((s, i) => (
            <Typography
              component="li"
              variant="body2"
              key={i}
              sx={{ lineHeight: 1.45 }}
            >
              {L(s)}
            </Typography>
          ))}
        </Box>
        {!hole && lesson.try && lesson.try.target && (
          <Typography
            variant="caption"
            color="text.secondary"
            component="div"
            sx={{ mt: 1 }}
          >
            {en
              ? 'The button is not on this screen right now — follow the steps.'
              : 'Нужной кнопки сейчас нет на экране — просто следуйте шагам.'}
          </Typography>
        )}
        <Box
          sx={{ display: 'flex', gap: 1, mt: 1.5, justifyContent: 'flex-end' }}
        >
          <Button
            size="small"
            onClick={() => {
              close();
              navigate(`/learn?open=${active}`);
            }}
            sx={{ textTransform: 'none' }}
          >
            {en ? 'Back to lessons' : 'К обучению'}
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={close}
            sx={{
              textTransform: 'none',
              borderRadius: '999px',
              fontWeight: 700,
            }}
          >
            {en ? 'Got it' : 'Понятно'}
          </Button>
        </Box>
      </Box>
    </Box>,
    document.body
  );
}
